<script setup>
import { ref, computed } from 'vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'

const search = ref('')
const pharmacies = computed(() => store.salesExecV2.pharmacies.value)
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return pharmacies.value.filter(p => !q || p.pharmacyName.toLowerCase().includes(q) || p.customerId.toLowerCase().includes(q))
})

function blankContact() { return { name: '', designation: '', mobile: '', email: '', notes: '' } }

// --- New Pharmacy (contacts as repeatable rows, same pattern as "+ Add
// Product" in the order-booking form) ---
const showNew = ref(false)
function blankPharmacyForm() {
  return {
    pharmacyName: '', ownerName: '', mobile1: '', city: '', territory: '', drugLicenseNo: '',
    dailyFootfall: 40, monthlyFootfall: 1080, category: 'C',
    contacts: [blankContact()],
  }
}
const form = ref(blankPharmacyForm())
function openNew() { form.value = blankPharmacyForm(); showNew.value = true }
function addContactRow() { form.value.contacts.push(blankContact()) }
function removeContactRow(i) { form.value.contacts.splice(i, 1) }
function submit() {
  const created = store.salesExecV2.createPharmacy({ ...form.value })
  if (created) {
    form.value.contacts.filter(c => c.name.trim()).forEach(c => store.salesExecV2.addContact(created.customerId, { ...c }))
    showNew.value = false
  }
}

// --- Pharmacy details drawer ---
const detailCustomerId = ref(null)
const detailPharmacy = computed(() => detailCustomerId.value ? pharmacies.value.find(p => p.customerId === detailCustomerId.value) || null : null)
function openDetail(p) { detailCustomerId.value = p.customerId }

// --- Add Person from Details (also repeatable rows, multiple in one go) ---
const showAddPerson = ref(false)
const personRows = ref([blankContact()])
function openAddPerson() { personRows.value = [blankContact()]; showAddPerson.value = true }
function addPersonRow() { personRows.value.push(blankContact()) }
function removePersonRow(i) { personRows.value.splice(i, 1) }
function submitPeople() {
  if (!detailCustomerId.value) return
  personRows.value.filter(c => c.name.trim()).forEach(c => store.salesExecV2.addContact(detailCustomerId.value, { ...c }))
  showAddPerson.value = false
}
</script>

<template>
  <div>
    <div class="card module-header" style="display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap;">
      <h2>{{ pharmacies.length }} pharmacies</h2>
      <button class="btn-primary" @click="openNew">+ New Pharmacy</button>
    </div>
    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search pharmacy or Customer ID…" />
        <span class="result-count">{{ filtered.length }} of {{ pharmacies.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Customer ID</th><th>Pharmacy</th><th>Owner</th><th>City</th><th>Mobile</th><th>Category</th></tr></thead>
          <tbody>
            <tr v-for="p in filtered" :key="p.customerId" class="clickable" @click="openDetail(p)">
              <td class="cell-muted">{{ p.customerId }}</td><td class="cell-strong">{{ p.pharmacyName }}</td>
              <td>{{ p.ownerName }}</td><td class="cell-muted">{{ p.city }}</td><td class="cell-muted">{{ p.mobile1 }}</td>
              <td class="cell-muted">{{ p.category }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pharmacy Details -->
    <DetailDrawer :open="!!detailPharmacy" :title="detailPharmacy?.pharmacyName || ''" @close="detailCustomerId=null">
      <template v-if="detailPharmacy">
        <dl style="display:grid;grid-template-columns:auto 1fr;gap:8px 16px;margin:0 0 20px;font-size:13px;">
          <dt class="cell-muted">Customer ID</dt><dd style="margin:0" class="cell-strong">{{ detailPharmacy.customerId }}</dd>
          <dt class="cell-muted">Owner</dt><dd style="margin:0">{{ detailPharmacy.ownerName }}</dd>
          <dt class="cell-muted">Drug License</dt><dd style="margin:0">{{ detailPharmacy.drugLicenseNo }}</dd>
          <dt class="cell-muted">Mobile</dt><dd style="margin:0">{{ detailPharmacy.mobile1 }}</dd>
          <dt class="cell-muted">City</dt><dd style="margin:0">{{ detailPharmacy.city }}</dd>
          <dt class="cell-muted">Territory</dt><dd style="margin:0">{{ detailPharmacy.territory }}</dd>
          <dt class="cell-muted">Category</dt><dd style="margin:0">{{ detailPharmacy.category }}</dd>
          <dt class="cell-muted">Daily Footfall</dt><dd style="margin:0">{{ detailPharmacy.dailyFootfall }}</dd>
          <dt class="cell-muted">Monthly Footfall</dt><dd style="margin:0">{{ detailPharmacy.monthlyFootfall }}</dd>
        </dl>

        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
          <div class="eyebrow">Contacts</div>
          <button class="filter-select" style="cursor:pointer;font-size:11px;" @click="openAddPerson">+ Add Person</button>
        </div>
        <div v-if="!detailPharmacy.contacts || !detailPharmacy.contacts.length" class="cell-muted" style="font-size:12.5px;margin-bottom:8px;">No contacts added yet.</div>
        <div v-for="(c,i) in detailPharmacy.contacts" :key="i" class="oos-remarks" style="margin-bottom:8px;">
          <strong style="display:block;font-size:13px;">{{ c.name }} <span v-if="c.designation" class="cell-muted" style="font-weight:400;">— {{ c.designation }}</span></strong>
          <div class="cell-muted" style="font-size:12px;">
            <span v-if="c.mobile">{{ c.mobile }}</span><span v-if="c.mobile && c.email"> · </span><span v-if="c.email">{{ c.email }}</span>
          </div>
          <div v-if="c.notes" style="font-size:12.5px;margin-top:4px;">{{ c.notes }}</div>
        </div>
      </template>
    </DetailDrawer>

    <!-- New Pharmacy -->
    <DetailDrawer :open="showNew" title="New Pharmacy" @close="showNew=false">
      <div class="form-group"><label>Pharmacy Name</label><input class="form-input" v-model="form.pharmacyName" /></div>
      <div class="form-group"><label>Owner Name</label><input class="form-input" v-model="form.ownerName" /></div>
      <div class="form-group"><label>Mobile</label><input class="form-input" v-model="form.mobile1" /></div>
      <div class="form-group"><label>Drug License No.</label><input class="form-input" v-model="form.drugLicenseNo" /></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group"><label>City</label><input class="form-input" v-model="form.city" /></div>
        <div class="form-group"><label>Territory</label><input class="form-input" v-model="form.territory" /></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group"><label>Daily Footfall</label><input class="form-input" type="number" v-model.number="form.dailyFootfall" /></div>
        <div class="form-group"><label>Monthly Footfall</label><input class="form-input" type="number" v-model.number="form.monthlyFootfall" /></div>
      </div>
      <div class="form-group">
        <label>Category</label>
        <select class="form-select" v-model="form.category"><option>A</option><option>B</option><option>C</option></select>
      </div>

      <div class="eyebrow" style="margin:20px 0 10px;">Contacts (optional)</div>
      <div v-for="(c,i) in form.contacts" :key="i" style="border:1px solid var(--color-border);border-radius:8px;padding:12px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <span class="cell-muted" style="font-size:11px;text-transform:uppercase;">Person {{ i+1 }}</span>
          <button class="filter-select" style="cursor:pointer;font-size:10.5px;padding:2px 8px;" @click="removeContactRow(i)" :disabled="form.contacts.length===1">×</button>
        </div>
        <div class="form-group" style="margin-bottom:8px;"><label>Name</label><input class="form-input" v-model="c.name" /></div>
        <div class="form-group" style="margin-bottom:8px;"><label>Designation</label><input class="form-input" v-model="c.designation" placeholder="e.g. Pharmacist-in-Charge" /></div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group" style="margin-bottom:8px;"><label>Number</label><input class="form-input" v-model="c.mobile" /></div>
          <div class="form-group" style="margin-bottom:8px;"><label>Email</label><input class="form-input" v-model="c.email" /></div>
        </div>
        <div class="form-group" style="margin:0;"><label>Notes</label><input class="form-input" v-model="c.notes" /></div>
      </div>
      <button class="filter-select" style="cursor:pointer;margin-bottom:16px;" @click="addContactRow">+ Add Person</button>

      <button class="btn-primary" :disabled="!form.pharmacyName || !form.ownerName || !form.mobile1 || !form.drugLicenseNo" @click="submit">Create Pharmacy</button>
    </DetailDrawer>

    <!-- Add Person (from Details) — same repeatable-row pattern -->
    <DetailDrawer :open="showAddPerson" title="Add Person" @close="showAddPerson=false">
      <div v-for="(c,i) in personRows" :key="i" style="border:1px solid var(--color-border);border-radius:8px;padding:12px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <span class="cell-muted" style="font-size:11px;text-transform:uppercase;">Person {{ i+1 }}</span>
          <button class="filter-select" style="cursor:pointer;font-size:10.5px;padding:2px 8px;" @click="removePersonRow(i)" :disabled="personRows.length===1">×</button>
        </div>
        <div class="form-group" style="margin-bottom:8px;"><label>Name</label><input class="form-input" v-model="c.name" /></div>
        <div class="form-group" style="margin-bottom:8px;"><label>Designation</label><input class="form-input" v-model="c.designation" placeholder="e.g. Pharmacist-in-Charge" /></div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group" style="margin-bottom:8px;"><label>Number</label><input class="form-input" v-model="c.mobile" /></div>
          <div class="form-group" style="margin-bottom:8px;"><label>Email</label><input class="form-input" v-model="c.email" /></div>
        </div>
        <div class="form-group" style="margin:0;"><label>Notes</label><input class="form-input" v-model="c.notes" /></div>
      </div>
      <button class="filter-select" style="cursor:pointer;margin-bottom:16px;" @click="addPersonRow">+ Add Person</button>

      <button class="btn-primary" :disabled="!personRows.some(c=>c.name.trim())" @click="submitPeople">Save</button>
    </DetailDrawer>
  </div>
</template>
